import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Home,
  MessageSquare,
  Globe,
  Search,
  Database,
  UserCog,
  SquareTerminal,
  ShieldCheck,
  LayoutDashboard,
} from 'lucide-react';
import { apiService, DASHBOARD_AUTH_CHANGED_EVENT, getDashboardToken } from '@/api/apiService';

export type AppNavItem = {
  name: string;
  href: string;
  icon: LucideIcon;
};

const ADMIN_NAV_ITEMS: AppNavItem[] = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Chat', href: '/chat', icon: MessageSquare },
  { name: 'Crawl', href: '/crawl', icon: Globe },
  { name: 'Search', href: '/search', icon: Search },
  { name: 'Sites', href: '/sites', icon: Database },
  { name: 'DB explorer', href: '/db-explorer', icon: SquareTerminal },
  { name: 'Preferences', href: '/preferences', icon: UserCog },
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Admin Panel', href: '/dashboard/admin', icon: ShieldCheck },
];

const CUSTOMER_NAV_ITEMS: AppNavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
];

/** App switcher in the outer sidebar — hidden when the user only has one destination. */
export function useAppNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const refreshRole = async () => {
      const token = getDashboardToken();
      if (!token) {
        if (!cancelled) setIsPlatformAdmin(false);
        return;
      }
      try {
        const payload = await apiService.dashboardMe();
        if (!cancelled) setIsPlatformAdmin(Boolean(payload?.user?.is_platform_admin));
      } catch {
        if (!cancelled) setIsPlatformAdmin(false);
      }
    };

    void refreshRole();
    const onAuthChanged = () => {
      void refreshRole();
    };
    window.addEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    return () => {
      cancelled = true;
      window.removeEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    };
  }, [pathname]);

  const navItems = useMemo(
    () => (isPlatformAdmin ? ADMIN_NAV_ITEMS : CUSTOMER_NAV_ITEMS),
    [isPlatformAdmin]
  );

  const showSidebar = navItems.length > 1;

  return { navItems, showSidebar, isPlatformAdmin };
}
